import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { ConfluenceCommentAri } from '@atlassian/ari/confluence/comment';

import { expValEquals } from '@confluence/feature-experiments';

import { useAgentSessionsContext } from './AgentSessionsContext.ts';
import {
	AGENT_MENTION_COMMENT_SAVED_EVENT,
	AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT,
	BACKGROUND_CONVERSATION_ACTIONS_RECONCILE_EVENT,
} from './agentMentionConstants.ts';
import { CommentAgentSessionsQuery } from './CommentAgentSessionsQuery.graphql.ts';
import type { CommentAgentSessionsQuery as CommentAgentSessionsQueryType } from './__generated__/CommentAgentSessionsQuery.graphql.ts';

// After an agent-mention comment is saved, the backend creates the SessionAssociationPublic
// asynchronously (once it has processed the chat-new trigger). We refetch the sessions query
// a few times at increasing offsets so the new comment's session connects without a page
// reload, regardless of how long the backend takes within this window.
export const SESSION_REFETCH_DELAYS_MS: number[] = [2000, 5000, 10000];

// The Content query defaults to current and archived pages when status is omitted. Include drafts
// explicitly so agent session status is also available while editing an unpublished page.
const CONTENT_STATUSES = ['current', 'draft', 'archived'];

/**
 * Rows requested per page. Skybridge's default *and* maximum page size for the underlying ERS
 * query is 100, so asking for more silently gets clamped — 100 is both the ceiling and the
 * value that reproduces the pre-paging request exactly.
 */
export const SESSION_PAGE_SIZE = 100;

/**
 * Hard cap on how many pages are read for one page load, including the first. Bounds the work
 * even if the server keeps reporting `hasNextPage` (or returns a non-advancing cursor), so a
 * bad response can never turn into an unbounded request loop.
 */
export const MAX_SESSION_PAGES = 5;

// ConversationState mirrors the BE AgentSessionPublicState enum.
// Defined inline here to avoid importing from useCommentAgentSession (PR 2).
export type ConversationState = 'WORKING' | 'ARTEFACT_READY' | 'INPUT_REQUIRED' | 'FAILED';

const VALID_CONVERSATION_STATES = new Set<string>([
	'WORKING',
	'ARTEFACT_READY',
	'INPUT_REQUIRED',
	'FAILED',
]);

// Terminal snapshot state the backend reports once the agent has finished its task (the
// ConvoAiAgentSessionState `COMPLETED` value). Per design, a completed session shows NO
// agent-status row, so buildSessionMap skips these sessions entirely (see below).
const COMPLETED_STATE = 'COMPLETED';
const CANCELLED_STATE = 'CANCELLED';

export type CommentAgentSessionMap = Map<
	string, // numeric comment ID extracted from subEntityId ARI
	{
		conversationId: string;
		state: ConversationState | null;
		/**
		 * Atlassian identity account id (aaid) of the mentioned agent, or null when absent.
		 * Drives the agent avatar (and its deterministic fallback) when no picture URL is present.
		 * Always set by buildSessionMap (null when the session carries no agent), so non-optional.
		 */
		agentId: string | null;
		/** Display name of the mentioned agent (or null) — used as the avatar's alt/aria text. */
		agentName: string | null;
		/** Absolute URL of the mentioned agent's avatar (picture), or null when unavailable. */
		avatarUrl: string | null;
	}
>;

type PageCommentAgentSessionsOptions = {
	/**
	 * When true on mount, or changed from false to true, schedules bounded discovery retries.
	 * Changing to false cancels pending retries, including those started by save/recovery events.
	 * Those events can still schedule new retries while false; keeping true does not repeat retries.
	 */
	refetchOnMount?: boolean;
};

type SessionsQueryResponse = CommentAgentSessionsQueryType['response'];

// Derived from the hook rather than imported as IEnvironment from relay-runtime: react-relay is
// this package's declared dependency, relay-runtime is not, and this cannot drift from whatever
// useRelayEnvironment actually returns.
type RelayEnvironment = ReturnType<typeof useRelayEnvironment>;

// A continued poll must not adopt an older recovery response after a newer live update.
// Track the existing imperative requests so it can wait for them to settle first.
const pendingSnapshotRequests = new WeakMap<RelayEnvironment, Map<string, number>>();

export function hasPendingSessionSnapshotRequest(
	environment: RelayEnvironment,
	pageId: string,
): boolean {
	return (pendingSnapshotRequests.get(environment)?.get(pageId) ?? 0) > 0;
}

function trackSessionSnapshotRequest(environment: RelayEnvironment, pageId: string): () => void {
	if (!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) return () => {};
	let requests = pendingSnapshotRequests.get(environment);
	if (!requests) {
		requests = new Map();
		pendingSnapshotRequests.set(environment, requests);
	}
	requests.set(pageId, (requests.get(pageId) ?? 0) + 1);
	return () => {
		const remaining = (requests.get(pageId) ?? 1) - 1;
		if (remaining) requests.set(pageId, remaining);
		else requests.delete(pageId);
	};
}

export type PageCommentAgentSessionsResult = {
	/** Map of comment id → session, built from every page fetched so far. */
	sessionMap: CommentAgentSessionMap;
	/** Source-comment conversations, including terminal sessions, for reopening Chat. */
	conversationMap: Map<string, string>;
	/**
	 * True only when the server positively answered "this page has no agent sessions": the
	 * content node resolved, the connection came back non-null, and it held zero rows.
	 *
	 * Deliberately NOT the same as `sessionMap.size === 0`. The map can be empty while sessions
	 * exist — every returned session was COMPLETED (filtered client-side), the connection was
	 * null (a partial GraphQL/hydration error), or the content node never resolved. Callers that
	 * take destructive action on emptiness (AgentSessionsFetcher deleting the per-user
	 * `cc-agent-mention-sessions` marker) must gate on this instead, because that marker also
	 * gates whether the fetcher mounts at all — so deleting it wrongly is sticky across reloads.
	 */
	hasConfirmedNoSessions: boolean;
};

// Stable empty value so the initial state keeps referential equality across renders.
const NO_EXTRA_PAGES: readonly SessionsQueryResponse[] = [];

/**
 * Reads the sessions connection off the (single) content node of a query response.
 * Returns null when the page or the connection did not resolve.
 */
function readSessionsConnection(
	data: SessionsQueryResponse | null | undefined,
): NonNullable<
	NonNullable<NonNullable<SessionsQueryResponse['content']>['nodes']>[number]
>['allCommentAgentSessions'] {
	for (const node of data?.content?.nodes ?? []) {
		if (node?.allCommentAgentSessions) {
			return node.allCommentAgentSessions;
		}
	}
	return null;
}

function hasOnlyTerminalSessions(data: SessionsQueryResponse): boolean {
	const connection = readSessionsConnection(data);
	return (
		connection?.pageInfo?.hasNextPage === false &&
		!!connection.edges?.length &&
		connection.edges.every((edge) => {
			const state = edge?.node?.agentSession?.state;
			return (
				state === COMPLETED_STATE ||
				(state === CANCELLED_STATE &&
					expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) ||
				state === 'ARTEFACT_READY' ||
				state === 'FAILED'
			);
		})
	);
}

/**
 * Extracts the numeric comment id from a session's `subEntityId`.
 *
 * `subEntityId` is contracted to be a Confluence comment ARI
 * (`ari:cloud:confluence:<cloudId>:comment/<commentId>`) and the map key must match the
 * `comment.id` used by ObjectComment / ObjectCommentThread. Parsing strictly means a value that
 * is not a comment ARI is dropped rather than contributing a bogus key — the historical
 * "everything after the last slash" rule would turn, say, a page ARI into `<pageId>`, which can
 * collide with a real comment id and attach an agent status row to the wrong comment.
 *
 * Returns null when `subEntityId` is not a well-formed Confluence comment ARI.
 */
function parseCommentIdFromSubEntityId(subEntityId: string): string | null {
	try {
		return ConfluenceCommentAri.parse(subEntityId).commentId;
	} catch {
		// ValidationError — not a Confluence comment ARI.
		return null;
	}
}

/**
 * Pre-guard behaviour: take everything after the last `/`, or the whole string when there is no
 * `/`. Retained verbatim for the experiment-off path so the ungated result is unchanged.
 */
function legacyCommentIdFromSubEntityId(subEntityId: string): string {
	const lastSlash = subEntityId.lastIndexOf('/');
	return lastSlash >= 0 ? subEntityId.slice(lastSlash + 1) : subEntityId;
}

/**
 * Builds a CommentAgentSessionMap from a Relay query result.
 * Pure data transformation — no hooks, no side effects.
 *
 * Unchanged from the pre-paging implementation apart from the gated comment-id extraction below:
 * with both experiments off this produces exactly the same map it always did.
 */
function buildSessionMap(
	data: SessionsQueryResponse,
	conversationMap?: Map<string, string>,
): CommentAgentSessionMap {
	const sessionMap: CommentAgentSessionMap = new Map();
	// Resolved once per call rather than per row: this runs on every render of every comment
	// surface, so evaluating the experiment inside the row loop would mean up to
	// SESSION_PAGE_SIZE gate reads per render for no benefit. Cleanup collapses this to
	// `parseCommentIdFromSubEntityId` directly.
	const extractCommentId =
		expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
		expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
			? parseCommentIdFromSubEntityId
			: legacyCommentIdFromSubEntityId;
	// Both loops are bounded: content.nodes is always a single page node, and
	// allCommentAgentSessions is bounded by the number of agent-mentioned comments
	// on the page — a small, user-driven number in practice.
	for (const node of data?.content?.nodes ?? []) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const edge of node?.allCommentAgentSessions?.edges ?? []) {
			const session = edge?.node;
			if (session?.conversationId && session?.subEntityId) {
				// subEntityId format: "ari:cloud:confluence:<cloudId>:comment/<commentId>"
				// Extract the numeric comment ID from the end so map keys match comment.id
				// used in ObjectComment / ObjectCommentThread.
				const commentId = extractCommentId(session.subEntityId);
				if (commentId == null) {
					continue;
				}
				// Keep the source association after completion without reviving its status row.
				conversationMap?.set(commentId, session.conversationId);
				const rawState = session.agentSession?.state;
				// The agent has finished its task (COMPLETED): render no status row at all. Skip the
				// session entirely — keeping it with a null state would fall back to WORKING in
				// useCommentAgentSession and leave a stray connector branch. COMPLETED arrives via
				// this snapshot on page load; useCommentAgentSession handles the equivalent live
				// subscription update after the agent's planned action runs.
				if (
					rawState === COMPLETED_STATE ||
					(rawState === CANCELLED_STATE &&
						expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true))
				) {
					continue;
				}
				const agent = session.agentSession?.agent;
				sessionMap.set(commentId, {
					conversationId: session.conversationId,
					state:
						rawState != null && VALID_CONVERSATION_STATES.has(rawState)
							? (rawState as ConversationState)
							: null,
					agentId: agent?.id ?? null,
					agentName: agent?.name ?? null,
					avatarUrl: agent?.picture ?? null,
				});
			}
		}
	}
	return sessionMap;
}

/**
 * Folds follow-on pages into the map built from page 1, mutating it in place.
 *
 * Only reachable with the experiment on, because `extraPages` is only ever populated by the gated
 * page walk. Pages are applied in server order, so a comment carrying sessions on two different
 * pages resolves last-one-wins — identical to how one un-truncated page would have resolved it.
 */
function mergeFollowOnPages(
	sessionMap: CommentAgentSessionMap,
	extraPages: readonly SessionsQueryResponse[],
	conversationMap?: Map<string, string>,
): CommentAgentSessionMap {
	// Bounded by MAX_SESSION_PAGES pages of SESSION_PAGE_SIZE rows.
	for (const page of extraPages) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const [commentId, session] of buildSessionMap(page, conversationMap)) {
			sessionMap.set(commentId, session);
		}
	}
	return sessionMap;
}

/**
 * True only when the server positively reported zero session rows — see
 * `PageCommentAgentSessionsResult.hasConfirmedNoSessions`.
 *
 * Fails safe: an unresolved content node or a null connection (which a partial GraphQL error
 * also produces) is "no answer", not "no sessions", and returns false.
 *
 * Pure: this only reports what the response said. Nothing acts on it unless the experiment is on
 * (see AgentSessionsFetcher), so computing it does not change behaviour on the control path.
 */
function readHasConfirmedNoSessions(
	data: SessionsQueryResponse,
	extraPages: readonly SessionsQueryResponse[],
): boolean {
	let sawConnection = false;
	// Bounded exactly as in buildSessionMap.
	for (const page of [data, ...extraPages]) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const node of page?.content?.nodes ?? []) {
			const connection = node?.allCommentAgentSessions;
			if (!connection) {
				continue;
			}
			sawConnection = true;
			if ((connection.edges ?? []).length > 0) {
				return false;
			}
		}
	}
	return sawConnection;
}

/**
 * A content signature for page 1 that changes whenever the first page's contents change.
 * Used to decide when the follow-on pages must be re-walked (a refetch that changes page 1
 * invalidates everything after it) without depending on the `data` object identity, which Relay
 * hands back fresh on every store update.
 */
function firstPageSignature(data: SessionsQueryResponse | null | undefined): string {
	const connection = readSessionsConnection(data);
	if (!connection) {
		return 'none';
	}
	const pageInfo = connection.pageInfo;
	return `${(connection.edges ?? []).length}:${pageInfo?.hasNextPage ? '1' : '0'}:${
		pageInfo?.endCursor ?? ''
	}`;
}

/**
 * Fetches one follow-on page. Resolves to null when the request fails or yields no payload —
 * paging is best-effort, so a failed page keeps whatever has already been read rather than
 * discarding the first page.
 */
async function fetchSessionsPage(
	environment: RelayEnvironment,
	pageId: string,
	after: string,
): Promise<SessionsQueryResponse | null> {
	const finishRequest = trackSessionSnapshotRequest(environment, pageId);
	try {
		const data = await fetchQuery<CommentAgentSessionsQueryType>(
			environment,
			CommentAgentSessionsQuery,
			{ pageId, status: CONTENT_STATUSES, first: SESSION_PAGE_SIZE, after },
			// New sessions and status changes can land beyond the first page.
			{
				fetchPolicy: expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
					? 'network-only'
					: 'store-or-network',
			},
		).toPromise();
		return data ?? null;
	} catch {
		return null;
	} finally {
		finishRequest();
	}
}

/**
 * Inner hook — only called when the experiment IS enabled.
 * Calls useLazyLoadQuery unconditionally (Rules of Hooks compliant) and
 * writes the result to AgentSessionsContext via the Provider in ObjectCommentContainer.
 *
 * Do NOT call this hook directly — use usePageCommentAgentSessions instead.
 */
export function usePageCommentAgentSessionsInner(
	pageId: string,
	options: PageCommentAgentSessionsOptions = {},
): PageCommentAgentSessionsResult {
	const { refetchOnMount = false } = options;
	const environment = useRelayEnvironment();
	const data = useLazyLoadQuery<CommentAgentSessionsQueryType>(CommentAgentSessionsQuery, {
		pageId,
		status: CONTENT_STATUSES,
		// Passed as variables so the query text is gate-independent. When both experiments are off
		// these stay null, which the server treats the same as omitting them — i.e. the default
		// (and maximum) page size of 100, exactly the pre-paging request. Ordered so the
		// experiments are evaluated last.
		first:
			expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
			expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
				? SESSION_PAGE_SIZE
				: null,
		after: null,
	});

	// Follow-on pages, read imperatively. They are kept out of the Relay-driven `data` above
	// because each page is a separate connection record (distinct `after` argument), so
	// useLazyLoadQuery only ever reads page 1.
	const [extraPages, setExtraPages] = useState<readonly SessionsQueryResponse[]>(NO_EXTRA_PAGES);
	const [paginationRefresh, setPaginationRefresh] = useState(0);

	// Close the loop on newly-mentioned agents: when an agent-mention comment is saved
	// (AGENT_MENTION_COMMENT_SAVED_EVENT, also used to open the CwR sidebar), refetch the
	// sessions into the Relay store so the new comment's session connects without a reload.
	// fetchQuery updates the store imperatively and useLazyLoadQuery re-reads it reactively,
	// so this never re-suspends or blanks the comment UI. We poll a few bounded times to
	// absorb the backend's async creation of the SessionAssociationPublic.
	const refetchTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	const clearRefetchTimers = useCallback(() => {
		refetchTimersRef.current.forEach(clearTimeout);
		refetchTimersRef.current = [];
	}, []);
	const scheduleSessionRefetches = useCallback(() => {
		// Supersede any in-flight poll from a previous save so timers don't pile up.
		clearRefetchTimers();
		// Keep checking beyond session creation when live updates are unavailable.
		const delays = expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
			? [...SESSION_REFETCH_DELAYS_MS, 15000, 20000, 30000]
			: SESSION_REFETCH_DELAYS_MS;
		const timers = delays.map((delay) =>
			setTimeout(() => {
				let finishRequest = () => {};
				fetchQuery<CommentAgentSessionsQueryType>(
					environment,
					CommentAgentSessionsQuery,
					{
						pageId,
						status: CONTENT_STATUSES,
						first:
							expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
							expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
								? SESSION_PAGE_SIZE
								: null,
						after: null,
					},
					{ fetchPolicy: 'network-only' },
					// Best-effort background refresh — swallow errors so a failed refetch
					// never surfaces; the next poll (or page reload) will recover.
				).subscribe({
					start: () => {
						finishRequest = trackSessionSnapshotRequest(environment, pageId);
					},
					complete: () => finishRequest(),
					next: (response) => {
						if (
							refetchTimersRef.current === timers &&
							expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
						) {
							setPaginationRefresh((version) => version + 1);
						}
						// Preserve the initial discovery window. Only a complete, non-empty
						// page of terminal sessions can end the remaining recovery checks.
						if (
							delay >= Math.max(...SESSION_REFETCH_DELAYS_MS) &&
							refetchTimersRef.current === timers &&
							hasOnlyTerminalSessions(response) &&
							expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)
						)
							clearRefetchTimers();
					},
					error: () => finishRequest(),
				});
			}, delay),
		);
		refetchTimersRef.current = timers;
	}, [clearRefetchTimers, environment, pageId]);
	useEffect(() => {
		if (!refetchOnMount) {
			return;
		}
		scheduleSessionRefetches();

		return clearRefetchTimers;
	}, [clearRefetchTimers, refetchOnMount, scheduleSessionRefetches]);

	useEffect(() => {
		window.addEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, scheduleSessionRefetches);
		window.addEventListener(AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT, scheduleSessionRefetches);
		window.addEventListener(
			BACKGROUND_CONVERSATION_ACTIONS_RECONCILE_EVENT,
			scheduleSessionRefetches,
		);
		return () => {
			window.removeEventListener(AGENT_MENTION_COMMENT_SAVED_EVENT, scheduleSessionRefetches);
			window.removeEventListener(AGENT_SESSION_SUBSCRIPTION_FAILED_EVENT, scheduleSessionRefetches);
			window.removeEventListener(
				BACKGROUND_CONVERSATION_ACTIONS_RECONCILE_EVENT,
				scheduleSessionRefetches,
			);
			clearRefetchTimers();
		};
	}, [clearRefetchTimers, scheduleSessionRefetches]);

	// Key the walk on page one's shape rather than object identity, so unrelated store updates
	// do not restart it. The gated refresh counter also restarts it after discovery polls.
	// Without a poll or a changed first-page signature, later-page additions still wait for
	// another walk (for example, on remount).
	const pageOneSignature = firstPageSignature(data);
	useEffect(() => {
		if (expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
			// A changed first page invalidates later pages; a poll alone keeps them visible.
			setExtraPages((prev) => (prev.length > 0 ? NO_EXTRA_PAGES : prev));
		}
	}, [environment, pageId, pageOneSignature]);

	// Refresh later pages after polling even when page one's cursors have not changed.
	// Keep the previous results visible while refreshing the same connection.
	useEffect(() => {
		if (!expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true)) {
			// Preserve the original reset-and-fetch effect in control. Returning prev avoids an
			// extra render when there are no follow-on pages to clear.
			setExtraPages((prev) => (prev.length > 0 ? NO_EXTRA_PAGES : prev));
		}
		const connection = readSessionsConnection(data);
		const firstCursor = connection?.pageInfo?.endCursor;
		// Evaluate the paging experiments last: only expose them for a truncated first page.
		if (
			!connection?.pageInfo?.hasNextPage ||
			!firstCursor ||
			!(
				expValEquals('platform_editor_inline_agentic_comments', 'isEnabled', true) ||
				expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
			)
		) {
			return;
		}

		let cancelled = false;
		void (async () => {
			const pages: SessionsQueryResponse[] = [];
			let after: string | null = firstCursor;
			// -1 because page 1 came from useLazyLoadQuery and already counts against the cap.
			for (let i = 0; i < MAX_SESSION_PAGES - 1; i++) {
				if (cancelled || after == null) {
					break;
				}
				const page = await fetchSessionsPage(environment, pageId, after);
				if (cancelled || page == null) {
					break;
				}
				pages.push(page);
				const nextPageInfo = readSessionsConnection(page)?.pageInfo;
				// Stop unless the server both reports more rows and hands back a cursor that
				// actually advanced — a repeated cursor would otherwise re-read the same page.
				const nextCursor = nextPageInfo?.endCursor;
				after = nextPageInfo?.hasNextPage && nextCursor && nextCursor !== after ? nextCursor : null;
			}
			if (!cancelled && pages.length > 0) {
				setExtraPages(pages);
			}
		})();

		return () => {
			cancelled = true;
		};
		// `data` is read for its contents only, and those are captured by pageOneSignature; using
		// `data` itself as a dep would restart the walk on every unrelated store update.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [environment, pageId, pageOneSignature, paginationRefresh]);

	// With both experiments off, the walk never runs and extraPages stays empty.
	const conversationMap = new Map<string, string>();
	const chatConversations = expValEquals(
		'platform_editor_inline_agentic_comments',
		'isEnabled',
		true,
	)
		? conversationMap
		: undefined;
	const sessionMap = buildSessionMap(data, chatConversations);
	return {
		sessionMap:
			extraPages.length > 0
				? mergeFollowOnPages(sessionMap, extraPages, chatConversations)
				: sessionMap,
		conversationMap,
		hasConfirmedNoSessions: readHasConfirmedNoSessions(data, extraPages),
	};
}

/**
 * Page-level hook that reads agent sessions for a given page.
 *
 * When the experiment is OFF: returns an empty Map immediately — no query, no Suspense.
 * When the experiment is ON: reads from AgentSessionsContext, which is populated by
 * AgentSessionsFetcher (mounted in ObjectCommentContainer only when experiment is on).
 *
 * Mount AgentSessionsFetcher at page level so the data is fetched once per page load
 * and shared across all comment instances via context.
 *
 * @returns A Map<commentId, { conversationId, state }>, or an empty Map if the
 *          experiment is off or no sessions exist.
 */
export function usePageCommentAgentSessions(): CommentAgentSessionMap {
	// When experiment is off, return immediately — zero queries, zero Suspense.
	// AgentSessionsFetcher (the component that calls useLazyLoadQuery) is never
	// mounted in ObjectCommentContainer when the gate is off, so this context
	// will always be the default empty Map in that case.
	return useAgentSessionsContext();
}
